// @ts-check

/**
 * @file Renders a project user assignment row.
 */

import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import {memo} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native-propforge"
import Button from "../../components/button"
import Card from "../../components/card"
import Text from "@kaspernj/api-maker/build/utils/text"
import useThemeColors, {useThemeName} from "../../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */

/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {object} ProjectUserAssignmentRowData
 * @property {string} id Assignment record id.
 * @property {{email: string, id: string, name: string}} user User shown in the row.
 */

/**
 * @typedef {object} ProjectUserAssignmentRowProps
 * @property {(assignmentId: string) => unknown} onRemovePress Called when the remove button is pressed.
 * @property {ProjectUserAssignmentRowData} row Assignment row data.
 * @property {string} testIDPrefix Prefix for row test ids.
 */

/** @typedef {Record<string, never>} ProjectUserAssignmentRowState */

/**
 * Displays one assigned project user with a remove action.
 * @augments {ShapeComponent<ProjectUserAssignmentRowProps, ProjectUserAssignmentRowState>}
 */
class ProjectUserAssignmentRow extends ShapeComponent {
  static propTypes = propTypesExact({
    onRemovePress: PropTypes.func.isRequired,
    row: PropTypes.exact({
      id: PropTypes.string.isRequired,
      user: PropTypes.exact({
        email: PropTypes.string.isRequired,
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired
      }).isRequired
    }).isRequired,
    testIDPrefix: PropTypes.string.isRequired
  })

  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()

  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /** Initializes locale and theme state. */
  setup() {
    useLocale()
    this.colors = useThemeColors()
    this.themeName = useThemeName()
  }

  /**
   * Renders the row.
   * @returns {ReactNode} Row content.
   */
  render() {
    const {row, testIDPrefix} = this.p
    const {colors, themeName} = this.tt
    return (
      <Card
        style={styles.row ||= {
          alignItems: "center",
          flexDirection: "row",
          justifyContent: "space-between",
          marginBottom: 8
        }}
        testID={`${testIDPrefix}/rowContainer-${row.id}`}
      >
        <View
          style={styles.rowText ||= {
            flex: 1
          }}
          testID={`${testIDPrefix}/row-${row.id}/text`}
        >
          <Text
            style={styles.rowName ||= {
              fontSize: 16,
              fontWeight: 600
            }}
            testID={`${testIDPrefix}/row-${row.id}/name`}
          >
            {row.user.name || row.user.email}
          </Text>
          <Text
            style={styles[`rowEmail-${themeName}`] ||= {
              color: colors.textMuted,
              fontSize: 13
            }}
            testID={`${testIDPrefix}/row-${row.id}/email`}
          >
            {row.user.email}
          </Text>
        </View>
        <Button
          iconName="trash"
          label={_("Remove")}
          onPress={this.tt.onRemovePress}
          testID={`${testIDPrefix}/row-${row.id}/removeButton`}
          variant="destructive"
        />
      </Card>
    )
  }

  /**
   * Removes the assignment represented by this row.
   * @returns {unknown} Remove handler result.
   */
  onRemovePress = () => {
    return this.p.onRemovePress(this.p.row.id)
  }
}

/** @type {NamedExoticComponent<ProjectUserAssignmentRowProps>} */
const ProjectUserAssignmentRowComponent = memo(shapeComponent(ProjectUserAssignmentRow))

export default ProjectUserAssignmentRowComponent
